import { color, Icon, OnyxGray, styled } from 'canoe';
import React, { useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import { colors } from 'styleguide';

import SearchSvg from '!!react-svg-loader!canoe/icons/assets/Search.svg';
import { Angle } from 'craft/icons';
import { StyledInput } from 'styleguide/components/Input/Select';

import Box from './Box';
import Label from './Label';

const Container = styled.div`
  margin-bottom: 8px;
`;

const Relative = styled.div`
  position: relative;
`;

const OptionList = styled.div`
  position: absolute;
  z-index: 2;
  margin-top: -1px;
  background: white;
  width: 100%;
  border: 1px solid ${colors('neutral', 3)};
  max-height: 224px;
  overflow-y: auto;
  border-bottom-left-radius: 4px;
  border-bottom-right-radius: 4px;
`;

interface ValueProps {
  hasValue: boolean;
}

const Value = styled.span<ValueProps>`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  color: ${(props: ValueProps) => (props.hasValue ? '#454b5e' : colors('neutral', 1))};
`;

export const OptionBox = styled<any>(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 8px;
  padding-right: 8px;
  cursor: pointer;
  border-top-left-radius: ${(props: any) => (props.noRadiusTop ? '0px' : '4px')};
  border-top-right-radius: ${(props: any) => (props.noRadiusTop ? '0px' : '4px')};
  border-bottom-left-radius: ${(props: any) => (props.noRadiusBottom ? '0px' : '4px')};
  border-bottom-right-radius: ${(props: any) => (props.noRadiusBottom ? '0px' : '4px')};
`;

const StyledSearch = styled.div`
  height: 40px;
  font-size: 12px;
  color: ${color(OnyxGray)};
  border-bottom: 2px solid ${colors('neutral', 2)};
  position: relative;

  > span {
    position: relative;
    left: 8px;
    top: 10px;
    width: 17px;
  }

  input {
    position: absolute;
    left: 30px;
    top: 1px;
    height: 36px;
    border: 0 none;
    width: calc(100% - 30px);
    padding-right: 10px;
    outline: none;

    &:focus,
    &:hover {
      border: 0 none;
    }
  }
`;

export const Option = styled.div`
  height: 32px;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding-left: 8px;
  font-size: 12px;
  color: ${color(OnyxGray)};
  border-bottom: 1px solid ${colors('neutral', 3)};
  text-overflow: ellipsis;

  &:hover {
    background: ${colors('neutral', 4)};
  }
`;

const FooterActionContainer = styled.button`
  appearance: none;
  outline: none;
  border: none;
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  background: none;
  color: ${colors('primary')};
  height: 32px;
  font-size: 12px;
  width: 100%;
  margin: none;

  &:hover {
    background: ${colors('neutral', 4)};
  }
`;

interface IndicatorProps {
  optionsVisible: boolean;
}

const Indicator = styled.div<IndicatorProps>`
  display: flex;
  align-items: center;
  gap: 4px;

  svg {
    transform: rotate(${(props: IndicatorProps) => (props.optionsVisible ? '180deg' : '0deg')});
    transition: all 0.1s ease-out 0s;
  }
`;

const getLabelFromValue = (options: OptionItem[], value: string) => {
  const found = options.find((option) => option.value === value);

  if (!found) {
    // if we cant find label, we should show value as is
    return value;
  }

  return found.name;
};

interface OptionItem {
  value?: any;
  name?: string;
}

interface FooterAction {
  label: string;
  onClick: () => void;
}

interface Props {
  value?: any;
  options: OptionItem[];
  onChange: (selectedValue: any) => void;
  label?: string;
  trackingId?: string;
  placeholder?: string;
  style?: any;
  footerAction?: FooterAction;
  noRadiusBottom?: boolean;
  noRadiusTop?: boolean;
  isSearchable?: boolean;
  onSearchChange?: (search: string) => void;
  boxCss?: Record<string, any>;
  searchInputProps?: Record<string, any>;
  suffix?: JSX.Element;
}

const DropdownBox = ({
  options,
  onChange,
  trackingId,
  value,
  label,
  placeholder,
  footerAction,
  noRadiusTop,
  noRadiusBottom,
  isSearchable,
  onSearchChange,
  boxCss,
  searchInputProps,
  suffix,
  ...props
}: Props) => {
  const [optionsVisible, setOptionsVisible] = useState(false);
  const [filter, setFilter] = useState(getLabelFromValue(options, value));

  const clearSearch = () => {
    if (onSearchChange) {
      onSearchChange?.('');
    }
    setFilter('');
  };

  const handleSearchChange = ({ target }: { target: OptionItem }) => {
    if (onSearchChange) {
      onSearchChange?.(target.value);
    }
    setFilter(target.value);
  };

  const onSelect = (selectedValue: any) => {
    onChange(selectedValue);
    setOptionsVisible(false);
  };

  const selectedLabel = options.find((item) => item.value === value)?.name;
  const selectedValue = selectedLabel || value;

  return (
    <Container {...props}>
      {label && <Label>{label}</Label>}
      <OutsideClickHandler
        onOutsideClick={() => {
          setOptionsVisible(false);
          clearSearch();
        }}
        disabled={!optionsVisible}
      >
        <Relative data-cy={`toolbox-property-${label}`}>
          <OptionBox
            noRadiusBottom={noRadiusBottom || !!optionsVisible}
            noRadiusTop={noRadiusTop}
            css={boxCss}
            onClick={() => {
              setOptionsVisible(!optionsVisible);
              clearSearch();
            }}
          >
            <Value hasValue={!!selectedValue}>{selectedValue || placeholder}</Value>
            <Indicator optionsVisible={optionsVisible}>
              <Angle color={colors('neutral', 2)} />
              {suffix}
            </Indicator>
          </OptionBox>
          {!!optionsVisible && (
            <OptionList data-cy={`${label}-option-list`}>
              {isSearchable && (
                <StyledSearch>
                  <Icon svg={SearchSvg} color="OnyxGrayL20" />
                  <StyledInput
                    type="text"
                    autoComplete="off"
                    placeholder={searchInputProps?.placeholder || 'Search Content'}
                    onChange={handleSearchChange}
                    name="search"
                    value={filter}
                    autoFocus
                    data-cy="search-input"
                  />
                </StyledSearch>
              )}
              {options.map(({ value: optionValue, name }) => (
                <Option
                  data-cy={`toolbox-property-${label}-${name}`}
                  key={name || optionValue}
                  onClick={() => onSelect(optionValue)}
                >
                  <span
                    css={{
                      overflow: 'hidden',
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {name || optionValue}
                  </span>
                </Option>
              ))}
              {footerAction && (
                <FooterActionContainer onClick={footerAction.onClick}>
                  {footerAction.label}
                </FooterActionContainer>
              )}
            </OptionList>
          )}
        </Relative>
      </OutsideClickHandler>
    </Container>
  );
};

export default DropdownBox;
